import { apiFetch } from "@/lib/sdk"

/** Client for /admin/terify/analytics/* (Terify's ShopifyQL-style query engine). */
export type FieldType = "text" | "number" | "money" | "percent" | "duration" | "decimal" | "ratio" | "date" | "bool"
export type Op = "is" | "is_not" | "contains" | "not_contains" | "gt" | "gte" | "lt" | "lte"
export type Filter = { field: string; op: Op; values: (string | number)[] }
export type RangeSpec = { preset?: string; since?: string; until?: string }
export type CompareSpec = "none" | "previous_period" | "previous_year" | "previous_year_dow" | { since: string; until: string }
export type Spec = {
  dataset: string
  metrics: string[]
  dimensions?: string[]
  filters?: Filter[]
  range?: RangeSpec
  compare?: CompareSpec
  sort?: { by: string; dir: "asc" | "desc" }
  limit?: number
  fill?: boolean
}
export type Column = { key: string; label: string; kind: "dimension" | "metric"; type: FieldType; description?: string; good?: "up" | "down"; /** Shown instead of "—" when null, e.g. "Missing cost". */ nullLabel?: string }
export type Row = Record<string, any> & { __previous?: Record<string, number | null> | null; __previous_key?: string | null }
export type Result = {
  columns: Column[]
  rows: Row[]
  totals: Record<string, number | null>
  previous_totals: Record<string, number | null> | null
  range: { since: string; until: string; label: string; grain: "hour" | "day" | "week" | "month" | "quarter" | "year" }
  compare_range: { since: string; until: string; label: string } | null
  query: string
  truncated: boolean
  generated_at: string
}
export type CatalogueField = { key: string; label: string; type: FieldType; description?: string; category?: string; good?: "up" | "down" }
export type Dataset = { key: string; label: string; description: string; time: boolean; metrics: CatalogueField[]; dimensions: CatalogueField[] }

const post = <T,>(path: string, body: unknown) => apiFetch<T>(path, { method: "POST", body: JSON.stringify(body) })

export const analytics = {
  query: (spec: Spec, fresh = false) => post<Result>("/admin/terify/analytics/query", { spec, fresh }),
  batch: (specs: Spec[], fresh = false) => post<{ results: (Result | { error: string })[] }>("/admin/terify/analytics/query", { specs, fresh }),
  catalogue: () => apiFetch<{ datasets: Dataset[] }>("/admin/terify/analytics/catalogue"),
  live: () => apiFetch<Live>("/admin/terify/analytics/live"),
  liveNow: () => apiFetch<{ visitors_right_now: number; as_of: string }>("/admin/terify/analytics/live/now"),
  subCohort: (body: { range?: RangeSpec; grain?: "week" | "month" | "quarter"; basis?: "orders" | "time"; metric?: string; origin?: "all" | "here" | "imported" }) => post<SubCohort>("/admin/terify/analytics/subscription-cohort", body),
  cohort: (body: { range?: RangeSpec; grain?: "week" | "month" | "quarter"; metric?: string }) => post<Cohort>("/admin/terify/analytics/cohort", body),
  ltvCohort: (body: { range?: RangeSpec }) => post<LtvCohort>("/admin/terify/analytics/profit-cohort", body),
  saved: () => apiFetch<{ reports: SavedReport[] }>("/admin/terify/analytics/saved"),
  savedOne: (id: string) => apiFetch<{ report: SavedReport }>(`/admin/terify/analytics/saved/${id}`),
  saveReport: (body: Omit<SavedReport, "id" | "created_by" | "created_by_name" | "created_at" | "updated_at">, id?: string) => post<{ report: SavedReport }>(`/admin/terify/analytics/saved${id ? `/${id}` : ""}`, body),
  deleteReport: (id: string) => apiFetch<{ deleted: boolean }>(`/admin/terify/analytics/saved/${id}`, { method: "DELETE" }),
  targets: () => apiFetch<{ targets: Target[] }>("/admin/terify/analytics/targets"),
  saveTarget: (body: Omit<Target, "id">, id?: string) => post<{ targets: Target[] }>(`/admin/terify/analytics/targets${id ? `/${id}` : ""}`, body),
  deleteTarget: (id: string) => apiFetch<{ targets: Target[] }>(`/admin/terify/analytics/targets/${id}`, { method: "DELETE" }),
  annotations: (since?: string, until?: string) => apiFetch<{ annotations: Annotation[] }>(`/admin/terify/analytics/annotations${since ? `?since=${since}&until=${until}` : ""}`),
  addAnnotation: (body: { date: string; note: string }) => post<{ annotations: Annotation[] }>("/admin/terify/analytics/annotations", body),
  deleteAnnotation: (id: string) => apiFetch(`/admin/terify/analytics/annotations/${id}`, { method: "DELETE" }),
  views: () => apiFetch<{ views: { report_key: string; viewed_at: string | null; pinned: boolean }[] }>("/admin/terify/analytics/views"),
  touchView: (key: string, body: { viewed?: boolean; pinned?: boolean }) => post(`/admin/terify/analytics/views/${key}`, body),
  dashboard: () => apiFetch<{ dashboard: DashboardLayout | null }>("/admin/terify/analytics/dashboard"),
  saveDashboard: (body: DashboardLayout | { reset: true }) => post<{ dashboard: DashboardLayout | null }>("/admin/terify/analytics/dashboard", body),
}

export type Viz = "line" | "bar" | "stacked_bar" | "horizontal_bar" | "area" | "donut" | "funnel" | "heatmap" | "metric" | "table"
export type SavedReport = { id: string; name: string; category: string; spec: Spec; viz: { type: Viz; items: number }; created_by: string; created_by_name: string; created_at: string; updated_at: string }
export type Target = { id: string; name: string; metric: string; value: number; period: "month" | "quarter" | "year" | "custom"; starts_at: string; ends_at: string }
export type Annotation = { id: string; date: string; note: string; created_by_name?: string }
export type DashboardLayout = { cards: { id: string; size: 1 | 2 | 3; section?: string }[] }
export type Live = {
  as_of: string
  since: string
  visitors_right_now: number
  sessions: number
  total_sales: number
  orders: number
  sparklines: { sessions: number[]; total_sales: number[]; orders: number[] }
  customer_behavior: { active_carts: number; checking_out: number; purchased: number }
  sessions_by_location: { country: string; region: string; city: string; sessions: number }[]
  customers: { new: number; returning: number }
  total_sales_by_product: { product: string; product_id: string | null; variant: string | null; total_sales: number; units: number }[]
  dots: { visitors: { lat: number; lng: number; n: number }[]; orders: { lat: number; lng: number; n: number }[] }
}
/** Cumulative contribution per starting subscriber by start month (null = not reached yet, or missing cost from missing_cost_from). */
export type LtvCohort = { range: { since: string; until: string; label: string }; columns: string[]; cohorts: { cohort: string; subscribers: number; values: (number | null)[]; missing_cost_from: number | null; months_elapsed: number }[] }
export type Cohort = { grain: string; metric: string; range: { since: string; until: string; label: string }; periods: number; cohorts: { cohort: string; customers: number; values: (number | null)[] }[] }
export type SubCohort = {
  grain: string
  basis: "orders" | "time"
  metric: string
  origin?: "all" | "here" | "imported"
  /** Imports left out of the order-number view: their order count on the previous store is unknown. */
  unknown_orders?: number
  range: { since: string; until: string; label: string }
  periods: number
  columns: string[]
  cohorts: { cohort: string; subscriptions: number; new_subscriptions?: number; imported?: number; values: (number | null)[] }[]
}
